import React from "react";
import "./Shimmer.css"; // Import styles for shimmer effects

// TableShimmer: Displays a loading placeholder for a table in the admin panel (white-grey theme)
export const TableShimmer = ({ columns, rowCount = 3 }) => {
  const defaultWidths = Array(columns.length).fill("100px"); // Default column width if not specified
  const columnWidths = columns.map(
    (col) => col.width || defaultWidths[columns.indexOf(col)]
  ); // Map column widths

  return (
    <table className="dashboard-table">
      {" "}
      {/* Table container with dashboard styling */}
      <thead>
        <tr>
          {columns.map((col, index) => (
            <th key={index} style={{ width: col.width || "auto" }}>
              {" "}
              {/* Table header with dynamic width */}
              <div
                className="shimmer"
                style={{ height: "20px", width: "80%" }}
              ></div>{" "}
              {/* Shimmer effect for header */}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {[...Array(rowCount)].map(
          (
            _,
            rowIndex // Render specified number of rows
          ) => (
            <tr key={rowIndex}>
              {columns.map(
                (
                  _,
                  colIndex // Render cells for each column
                ) => (
                  <td key={colIndex}>
                    <div
                      className="shimmer"
                      style={{ height: "20px", width: columnWidths[colIndex] }}
                    ></div>{" "}
                    {/* Shimmer effect for cell */}
                  </td>
                )
              )}
            </tr>
          )
        )}
      </tbody>
    </table>
  );
};

// CardShimmer: Displays a loading placeholder for cards in the admin panel
export const CardShimmer = ({
  count = 3,
  width = "100%",
  height = "300px",
}) => {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
        gap: "24px",
      }}
    >
      {" "}
      {/* Grid layout for cards */}
      {[...Array(count)].map(
        (
          _,
          index // Render specified number of cards
        ) => (
          <div
            key={index}
            className="shimmer"
            style={{
              width,
              height,
              borderRadius: "8px",
              overflow: "hidden",
            }} // Card container with shimmer effect
          >
            <div style={{ height: "50%", width: "100%" }} className="shimmer" />{" "}
            {/* Shimmer for card image */}
            <div style={{ padding: "16px" }}>
              {" "}
              {/* Card content container */}
              <div
                className="shimmer"
                style={{ height: "20px", width: "80%", marginBottom: "12px" }}
              />{" "}
              {/* Shimmer for title */}
              <div
                className="shimmer"
                style={{ height: "40px", width: "100%", marginBottom: "12px" }}
              />{" "}
              {/* Shimmer for main content */}
              <div
                className="shimmer"
                style={{ height: "16px", width: "60%" }}
              />{" "}
              {/* Shimmer for text line 1 */}
              <div
                className="shimmer"
                style={{ height: "16px", width: "70%", marginTop: "8px" }}
              />{" "}
              {/* Shimmer for text line 2 */}
              <div
                className="shimmer"
                style={{ height: "16px", width: "50%", marginTop: "8px" }}
              />{" "}
              {/* Shimmer for text line 3 */}
            </div>
          </div>
        )
      )}
    </div>
  );
};

// ChatListShimmer: Displays a loading placeholder for a chat list in the admin panel (left sidebar)
export const ChatListShimmer = ({ count = 5 }) => {
  return (
    <div className="chatroom-shimmer">
      {" "}
      {/* Container for chat list shimmer */}
      {[...Array(count)].map(
        (
          _,
          index // Render specified number of chat items
        ) => (
          <div className="shimmer-item" key={index}>
            <div className="shimmer shimmer-name" />{" "}
            {/* Shimmer for chat name */}
            <div className="shimmer shimmer-message" />{" "}
            {/* Shimmer for chat message preview */}
          </div>
        )
      )}
    </div>
  );
};

// SubscriptionCardShimmer: Displays a loading placeholder for subscription cards on the client website (black theme by default)
export const SubscriptionCardShimmer = ({ count = 3, variant = "dark" }) => (
  <div
    className={`subscription-card-shimmer-wrapper ${variant}`}
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
      gap: "24px",
      width: "100%",
    }} // Grid layout for subscription cards
  >
    {[...Array(count)].map(
      (
        _,
        index // Render specified number of cards
      ) => (
        <div className={`subscription-card-shimmer ${variant}`} key={index}>
          <div className="shimmer shimmer-title" />{" "}
          {/* Shimmer for card title */}
          <div className="shimmer shimmer-price" /> {/* Shimmer for price */}
          <div className="shimmer shimmer-description" />{" "}
          {/* Shimmer for description */}
          <div className="shimmer shimmer-feature" />{" "}
          {/* Shimmer for feature list */}
          <div className="shimmer shimmer-button" />{" "}
          {/* Shimmer for action button */}
        </div>
      )
    )}
  </div>
);

// SubscriptionDetailsShimmer: Displays a loading placeholder for subscription details with left and right columns
export const SubscriptionDetailsShimmer = ({ variant = "dark" }) => {
  return (
    <div className={`subscription-details-shimmer-wrapper ${variant}`}>
      {" "}
      {/* Container for subscription details shimmer */}
      {/* Left Sidebar */}
      <div className="shimmer-column shimmer-left">
        {" "}
        {/* Left column for sidebar */}
        {[...Array(5)].map(
          (
            _,
            index // Render 5 sidebar items
          ) => (
            <div
              key={index}
              className="shimmer shimmer-sidebar-item"
            /> /* Shimmer for sidebar item */
          )
        )}
      </div>
      {/* Right Content */}
      <div className="shimmer-column shimmer-right">
        {" "}
        {/* Right column for main content */}
        <div className="shimmer shimmer-heading" /> {/* Shimmer for heading */}
        {[...Array(15)].map(
          (
            _,
            index // Render 15 paragraph lines
          ) => (
            <div
              key={index}
              className="shimmer shimmer-paragraph-line"
            /> /* Shimmer for paragraph line */
          )
        )}
      </div>
    </div>
  );
};

// ProfileDetailsShimmer: Displays a loading placeholder for profile page layout with left and right columns
export const ProfileDetailsShimmer = ({ variant = "dark" }) => {
  return (
    <div className={`profile-details-shimmer-wrapper container ${variant}`}>
      {" "}
      {/* Container for profile shimmer */}
      {/* Left Sidebar */}
      <div className="shimmer-column shimmer-profile-left">
        {" "}
        {/* Left column for sidebar */}
        {[...Array(2)].map(
          (
            _,
            i // Render 2 sidebar items
          ) => (
            <div
              key={i}
              className="shimmer shimmer-sidebar-item"
            /> /* Shimmer for sidebar item */
          )
        )}
      </div>
      {/* Right Form Section */}
      <div className="shimmer-column shimmer-profile-right">
        {" "}
        {/* Right column for form section */}
        <div
          className="shimmer shimmer-heading"
          style={{ width: "40%" }}
        />{" "}
        {/* Shimmer for heading */}
        {[...Array(7)].map(
          (
            _,
            i // Render 7 form fields
          ) => (
            <div
              key={i}
              className="shimmer shimmer-form-field"
            /> /* Shimmer for form field */
          )
        )}
      </div>
    </div>
  );
};

// ChatShimmer: Displays a loading placeholder for chat messages (WhatsApp-like bubble loader)
export function ChatShimmer({ count = 6 }) {
  return (
    <div className="chat-shimmer-wrapper">
      {" "}
      {/* Container for chat message shimmer */}
      {[...Array(count)].map((_, index) => {
        // Render specified number of chat bubbles
        const isSender = index % 2 === 0; // Alternate between sent and received messages

        if (isSender) {
          return (
            <div key={index} className="chat-shimmer-bubble sent shimmer">
              {" "}
              {/* Shimmer for sent message */}
              <div className="shimmer-bubble-line shimmer" />{" "}
              {/* Shimmer for message line 1 */}
              <div
                className="shimmer-bubble-line shimmer"
                style={{ width: "70%" }}
              />{" "}
              {/* Shimmer for message line 2 */}
            </div>
          );
        }

        return (
          <div key={index} className="chat-shimmer-row">
            {" "}
            {/* Row for received message */}
            <div className="shimmer-avatar shimmer" />{" "}
            {/* Shimmer for user avatar */}
            <div className="chat-shimmer-content">
              {" "}
              {/* Container for received message content */}
              <div className="chat-shimmer-bubble received shimmer">
                {" "}
                {/* Shimmer for received message */}
                <div className="shimmer-bubble-line shimmer" />{" "}
                {/* Shimmer for message line 1 */}
                <div
                  className="shimmer-bubble-line shimmer"
                  style={{ width: "60%" }}
                />{" "}
                {/* Shimmer for message line 2 */}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
